import {
    faBusinessTime,
    faFilePen,
    faPlane,
    faPlaneArrival,
    faPlaneCircleExclamation,
    faPlaneCircleXmark,
    faPlaneDeparture,
    faFileExport,
    faDownload,
    faPaperPlane,
    faFileImport,
    faRoute,
    faCheckCircle,
    faMapMarkedAlt,
    faFileAlt,
    faShareAlt
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState, useEffect } from "react";
import './manager.scss';
import { Fleet } from "../fleet/fleet";
import { useTranslation } from "react-i18next";
import { ManualReport } from "../manual-report/manual-report";
import { Dispatcher } from "../dispatcher/dispatcher";
import api from "../../api/api";
import {FreeMode} from "../free-mode/free-mode";
import {CharterFlight} from "../charter-flight/charter-flight";
import {RegularFlight} from "../regular-flight/regular-flight";
import { IvaoImport } from "../ivao-import/ivao-import";
import { useCustomAlert } from "../../components/custom-alert";
import { useLocation } from "react-router-dom";

export function Manager() {
    const location = useLocation();
    const isFromTour = location.state?.mode === 'tour';
    const [selectedButton, setSelectedButton] = useState(isFromTour ? 'tour' : null);
    const [config, setConfig] = useState({
        ALLOW_CHARTER: false,
        ALLOW_AUTOMATIC_REPORT: false,
        ALLOW_MANUAL_REPORT: false,
        ALLOW_FREE_MODE: false,
        ALLOW_REGULAR: false,
    });
    const [error, setError] = useState(null);
    const [activeFlight, setActiveFlight] = useState(null);
    const [loading, setLoading] = useState(false);
    const [importingSimbrief, setImportingSimbrief] = useState(false);
    const [sendingToIVAO, setSendingToIVAO] = useState(false);
    const [sendingToVATSIM, setSendingToVATSIM] = useState(false);
    // Removed polling states due to infinite loop issues
    const [simbriefAccount, setSimbriefAccount] = useState(null);

    const { t } = useTranslation();
    const { showSuccess, showError, showWarning, showInfo } = useCustomAlert();

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const [charter, automatic, manual, free, regular] = await Promise.all([
                    api.get('/configs/ALLOW_CHARTER'),
                    api.get('/configs/ALLOW_AUTOMATIC_REPORT'),
                    api.get('/configs/ALLOW_MANUAL_REPORT'),
                    api.get('/configs/ALLOW_FREE_MODE'),
                    api.get('/configs/ALLOW_REGULAR'),
                ]);
                setConfig({
                    ALLOW_CHARTER: charter.data.isActive || false,
                    ALLOW_AUTOMATIC_REPORT: automatic.data.isActive || false,
                    ALLOW_MANUAL_REPORT: manual.data.isActive || false,
                    ALLOW_FREE_MODE: free.data.isActive || false,
                    ALLOW_REGULAR: regular.data.isActive || false,
                });
            } catch (error) {
                console.error('Failed to fetch configuration:', error);
                setError(t('config-error-default'));
            }
        };

        const fetchUserSimBriefAccount = async () => {
            try {
                const user = JSON.parse(localStorage.getItem('user'));
                if (user?.id) {
                    const response = await api.get('/pilots/authenticated');
                    const userPilot = response.data.find(pilot => pilot.id === user.id);
                    if (userPilot?.simbriefAccount) {
                        setSimbriefAccount(userPilot.simbriefAccount);
                    }
                }
            } catch (error) {
                console.error('Failed to fetch user SimBrief account:', error);
            }
        };

        fetchConfig();
        fetchUserSimBriefAccount();
        
        // Auto-scroll to content if coming from tours
        if (isFromTour) {
            setTimeout(() => {
                const contentElement = document.querySelector('.manager-content');
                if (contentElement) {
                    contentElement.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'start',
                        inline: 'nearest'
                    });
                }
            }, 500);
        }
    }, [t, isFromTour]);

    const fetchActiveFlight = async () => {
        setLoading(true);
        try {
            const response = await api.get('/flights/flight-active');
            if (response?.data && response.data.length > 0) {
                const flight = response.data[0];
                
                // Both extraData and simbriefData are now parsed from JSON by the API
                
                setActiveFlight(flight);
            } else {
                setActiveFlight(null);
            }
            setError(null);
        } catch (error) {
            console.error('Failed to fetch active flight:', error);
            const errorMessage =
                error.response?.data?.error || error.message || t('failed-to-fetch-active-flight');
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchActiveFlight();
        const interval = setInterval(fetchActiveFlight, 30000);
        return () => clearInterval(interval);
    }, [t]);


    const handleCancelFlight = async (flightId) => {
        try {
            await api.delete(`/flights/${flightId}`);
            setActiveFlight(null);
            setError(null);
        } catch (error) {
            console.error('Failed to cancel flight:', error);
            const errorMessage =
                error.response?.data?.error || error.message || t('failed-to-cancel-flight');
            setError(errorMessage);
        }
    };

    const handleImportSimBrief = async (flightId) => {
        setImportingSimbrief(true);
        try {
            const response = await api.post(`/simbrief/import/${flightId}`);
            if (response?.data) {
                const message = response.data.simbrief?.pdfLink 
                    ? t('SimBrief data imported successfully. PDF OFP available.')
                    : t('SimBrief data imported successfully');
                showSuccess(message);
                await fetchActiveFlight();
            }
        } catch (error) {
            console.error('Failed to import SimBrief data:', error);
            let errorMessage = error.response?.data?.message || error.response?.data?.error || t('Failed to import SimBrief data');
            
            // Add helpful hints for common errors
            if (error.response?.data?.error === 'No active SimBrief OFP found') {
                errorMessage = t('No SimBrief OFP found. Please:\n1. Click "Create in SimBrief" to open SimBrief\n2. Generate the OFP in SimBrief\n3. Then click "Import from SimBrief"');
            }
            
            showError(errorMessage);
        } finally {
            setImportingSimbrief(false);
        }
    };

    const handleSendToIVAO = async (flightId) => {
        setSendingToIVAO(true);
        try {
            const response = await api.get(`/simbrief/ivao-prefile/${flightId}`);
            if (response?.data?.ivaoPrefileUrl) {
                window.open(response.data.ivaoPrefileUrl, '_blank');
                showInfo(t('IVAO prefile page opened. Please complete the filing on IVAO website.'));
            } else {
                showError(t('Failed to generate IVAO prefile URL'));
            }
        } catch (error) {
            console.error('Failed to send to IVAO:', error);
            const errorMessage = error.response?.data?.error || t('Failed to generate IVAO flight plan');
            showError(errorMessage);
        } finally {
            setSendingToIVAO(false);
        }
    };

    const handleSendToVATSIM = async (flightId) => {
        setSendingToVATSIM(true);
        try {
            const response = await api.get(`/simbrief/vatsim-prefile/${flightId}`);
            if (response?.data?.url) {
                window.open(response.data.url, '_blank');
                showInfo(t('VATSIM prefile page opened. Please complete the filing on VATSIM website.'));
            } else {
                showError(t('Failed to generate VATSIM prefile URL'));
            }
        } catch (error) {
            console.error('Failed to send to VATSIM:', error);
            const errorMessage = error.response?.data?.error || t('Failed to generate VATSIM flight plan');
            showError(errorMessage);
        } finally {
            setSendingToVATSIM(false);
        }
    };

    // SimBrief auto-polling removed due to infinite loop issues
    // Import button still works perfectly for manual import

    const buttonContent = {
        charter: <CharterFlight onFlightSubmit={fetchActiveFlight} />,
        regular: <RegularFlight onFlightSubmit={fetchActiveFlight} />,
        manual: <ManualReport />,
        free: <FreeMode 
            onFlightSubmit={fetchActiveFlight} 
            activeFlight={activeFlight}
        />,
        tour: <FreeMode 
            onFlightSubmit={fetchActiveFlight} 
            activeFlight={activeFlight}
            isFromTour={true}
            initialData={{
                departure: location.state?.departure,
                arrival: location.state?.arrival,
                tourName: location.state?.tourName,
                legOrder: location.state?.legOrder
            }}
        />,
        dispatcher: <Dispatcher />,
        fleet: <Fleet />,
        ivao: <IvaoImport />
    };

    const handleButtonClick = (buttonType) => {
        setSelectedButton(buttonType);
        
        // Smooth scroll to the content area after a short delay
        setTimeout(() => {
            const contentElement = document.querySelector('.manager-content');
            if (contentElement) {
                contentElement.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'start',
                    inline: 'nearest'
                });
            }
        }, 100);
    };

    return (
        <>
            <div className="manager-title">
                {/* Flight Progress Steps integrated in title */}
                {activeFlight && (
                    <div className="flight-progress-integrated">
                        <div className={`progress-bar-container step-${
                            activeFlight.simbriefData && activeFlight.startFlight ? 4 :
                            activeFlight.simbriefData ? 3 :
                            activeFlight.startFlight ? 4 : 2
                        }`}>
                            <div className="progress-steps">
                                <div className="progress-step completed">
                                    <div className="step-icon">
                                        <FontAwesomeIcon icon={faCheckCircle} />
                                    </div>
                                    <span className="step-title">1. Planear</span>
                                </div>
                                
                                <div className={`progress-step ${activeFlight.simbriefData ? 'completed' : 'active'}`}>
                                    <div className="step-icon">
                                        <FontAwesomeIcon icon={activeFlight.simbriefData ? faCheckCircle : faFileAlt} />
                                    </div>
                                    <span className="step-title">2. SimBrief</span>
                                </div>
                                
                                <div className={`progress-step ${activeFlight.simbriefData ? (activeFlight.startFlight ? 'completed' : 'active') : ''}`}>
                                    <div className="step-icon">
                                        <FontAwesomeIcon icon={activeFlight.startFlight ? faCheckCircle : faShareAlt} />
                                    </div>
                                    <span className="step-title">3. FPL</span>
                                </div>
                                
                                <div className={`progress-step ${activeFlight.startFlight ? 'active' : ''}`}>
                                    <div className="step-icon">
                                        <FontAwesomeIcon icon={faPlane} />
                                    </div>
                                    <span className="step-title">4. Volar</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                
                {!activeFlight && (
                    <>
                        <h1>
                            <FontAwesomeIcon icon={faPlane} /> {t('manager-title')}
                        </h1>
                        <p className="flight-session-message">
                            {t('flight-session-message') || '¡Bienvenido a tu centro de operaciones! Aquí es donde comienza tu aventura en los cielos. Prepara tu plan de vuelo y despega hacia tu próximo destino.'}
                        </p>
                    </>
                )}
                
                {error && <p className="error">{error}</p>}
                {loading ? (
                    <p>{t('loading')}</p>
                ) : (
                    <>
                        {activeFlight ? (
                            <>
                                <div className="active-flight">
                                    <h3>{t('active-flight')}</h3>
                                    
                                    {/* Ready for Departure and Cancel buttons */}
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '1rem',
                                        padding: '0.75rem',
                                        backgroundColor: 'var(--background-primary)',
                                        borderRadius: '6px',
                                        border: '1px solid var(--border-color)'
                                    }}>
                                        <div style={{
                                            fontSize: '1.1rem',
                                            fontWeight: 'bold',
                                            color: 'var(--text-primary)'
                                        }}>
                                            {activeFlight.startFlight === null && activeFlight.closeFlight === null
                                                ? '🛫 ' + t('Ready for Departure')
                                                : '✈️ ' + t('In Flight')}
                                        </div>
                                        <button
                                            className="btn danger"
                                            onClick={() => handleCancelFlight(activeFlight.id)}
                                        >
                                            {t('cancel-flight')}
                                        </button>
                                    </div>
                                    
                                    {activeFlight.departureIcao && activeFlight.arrivalIcao && (
                                        <div className="flight-actions">
                                            <div className="simbrief-button-container">
                                                <button
                                                    className="btn"
                                                    onClick={() => window.open((() => {
                                                        let url = `https://dispatch.simbrief.com/options/custom?airline=${activeFlight.callsign.substring(0,3)}&fltnum=${activeFlight.callsign.substring(3)}`;
                                                        
                                                        // Add aircraft type
                                                        if (activeFlight.aircraft && activeFlight.aircraft !== 'ZZZZ') {
                                                            url += `&type=${activeFlight.aircraft}`;
                                                        }
                                                        
                                                        // Add route
                                                        url += `&orig=${activeFlight.departureIcao}&dest=${activeFlight.arrivalIcao}`;
                                                        
                                                        // Check for passengers from SimBrief data or comment field
                                                        let passengers = null;
                                                        let extraFuel = null;
                                                        
                                                        // First check SimBrief data
                                                        if (activeFlight.simbriefData?.weights?.pax_count) {
                                                            passengers = activeFlight.simbriefData.weights.pax_count;
                                                        }
                                                        
                                                        // Then check extraData field for Free Mode data
                                                        if (activeFlight.extraData) {
                                                            if (activeFlight.extraData.passengers) {
                                                                passengers = activeFlight.extraData.passengers;
                                                            }
                                                            if (activeFlight.extraData.extraFuelMinutes) {
                                                                extraFuel = activeFlight.extraData.extraFuelMinutes;
                                                            }
                                                        }
                                                        
                                                        // Add passengers if available
                                                        if (passengers) {
                                                            url += `&pax=${passengers}`;
                                                        }
                                                        
                                                        // Add extra fuel if available (in minutes)
                                                        if (extraFuel) {
                                                            url += `&addedfuel=${extraFuel}&addedfuel_units=min`;
                                                        }
                                                        
                                                        // Add remarks
                                                        url += `&manualrmk=RMK%2FTCAS+EQUIPPED+IVAOVA%2F${activeFlight.callsign.substring(0,3)}&acdata=%7B%22extrarmk%22%3A%22RMK%2FTCAS+EQUIPPED+IVAOVA%2F${activeFlight.callsign.substring(0,3)}%22%7D`;
                                                        
                                                        return url;
                                                    })(), '_blank')}
                                                >
                                                    <FontAwesomeIcon icon={faFileExport} style={{ marginRight: '0.5rem' }} />
                                                    <span>{t('create-simbrief-flightplan')}</span>
                                                </button>
                                                <button 
                                                    className="btn"
                                                    onClick={() => handleImportSimBrief(activeFlight.id)}
                                                    title={t('Import SimBrief data')}
                                                    disabled={importingSimbrief}
                                                >
                                                    <FontAwesomeIcon icon={faDownload} style={{ marginRight: '0.5rem' }} />
                                                    <span>
                                                        {importingSimbrief 
                                                            ? t('Importing...') 
                                                            : activeFlight.simbriefData 
                                                                ? t('Re-import from SimBrief')
                                                                : t('Import from SimBrief')
                                                        }
                                                    </span>
                                                </button>
                                                {activeFlight.simbriefData && (
                                                    <>
                                                        {activeFlight.network === 'IVAO' && (
                                                            <button
                                                                className="btn"
                                                                onClick={() => handleSendToIVAO(activeFlight.id)}
                                                                title={t('Send flight plan to IVAO')}
                                                                disabled={sendingToIVAO}
                                                            >
                                                                <FontAwesomeIcon icon={faPaperPlane} style={{ marginRight: '0.5rem' }} />
                                                                <span>{sendingToIVAO ? t('Sending...') : t('Send to IVAO')}</span>
                                                            </button>
                                                        )}
                                                        {activeFlight.network === 'VATSIM' && (
                                                            <button
                                                                className="btn"
                                                                onClick={() => handleSendToVATSIM(activeFlight.id)}
                                                                title={t('Send flight plan to VATSIM')}
                                                                disabled={sendingToVATSIM}
                                                            >
                                                                <FontAwesomeIcon icon={faPaperPlane} style={{ marginRight: '0.5rem' }} />
                                                                <span>{sendingToVATSIM ? t('Sending...') : t('Send to VATSIM')}</span>
                                                            </button>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                            
                                            {/* Removed auto-polling UI due to issues - Import button still works */}
                                        </div>
                                    )}
                                    {/* Flight Information */}
                                    <div style={{ 
                                        padding: '1rem',
                                        backgroundColor: 'var(--background-secondary)',
                                        borderRadius: '8px',
                                        border: '1px solid var(--border-color)',
                                        marginBottom: '1rem'
                                    }}>
                                        <h4 style={{ 
                                            margin: '0 0 1rem 0', 
                                            color: 'var(--accent-primary)',
                                            fontSize: '1rem'
                                        }}>
                                            ✈️ {t('Flight Information')}
                                        </h4>
                                        
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem', fontSize: '0.95rem' }}>
                                            <div style={{ 
                                                padding: '0.5rem', 
                                                backgroundColor: 'var(--background-primary)', 
                                                borderRadius: '4px'
                                            }}>
                                                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                                                    {t('callsign')}
                                                </div>
                                                <div style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>
                                                    {activeFlight.callsign}
                                                </div>
                                            </div>
                                            
                                            <div style={{ 
                                                padding: '0.5rem', 
                                                backgroundColor: 'var(--background-primary)', 
                                                borderRadius: '4px'
                                            }}>
                                                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                                                    {t('central-aircraft')}
                                                </div>
                                                <div style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>
                                                    {activeFlight.aircraft}
                                                </div>
                                            </div>
                                            
                                            <div style={{ 
                                                padding: '0.5rem', 
                                                backgroundColor: 'var(--background-primary)', 
                                                borderRadius: '4px'
                                            }}>
                                                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                                                    {t('central-departure')}
                                                </div>
                                                <div style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>
                                                    {activeFlight.departure.name} ({activeFlight.departureIcao})
                                                </div>
                                            </div>
                                            
                                            <div style={{ 
                                                padding: '0.5rem', 
                                                backgroundColor: 'var(--background-primary)', 
                                                borderRadius: '4px'
                                            }}>
                                                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                                                    {t('central-arrival')}
                                                </div>
                                                <div style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>
                                                    {activeFlight.arrival.name} ({activeFlight.arrivalIcao})
                                                </div>
                                            </div>
                                            
                                            <div style={{ 
                                                padding: '0.5rem', 
                                                backgroundColor: 'var(--background-primary)', 
                                                borderRadius: '4px',
                                                gridColumn: 'span 2'
                                            }}>
                                                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                                                    {t('network')}
                                                </div>
                                                <div style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>
                                                    {activeFlight.network}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Show SimBrief data if imported */}
                                    {activeFlight.simbriefData && (
                                        <div className="simbrief-info" style={{ 
                                            marginTop: '1rem', 
                                            padding: '1rem', 
                                            backgroundColor: 'var(--background-secondary)', 
                                            borderRadius: '8px',
                                            border: '1px solid var(--border-color)'
                                        }}>
                                            <h4 style={{ margin: '0 0 1rem 0', color: 'var(--accent-primary)' }}>
                                                📋 {t('Operational Flight Plan')}
                                            </h4>
                                            
                                            <div style={{ marginBottom: '1rem' }}>
                                                <div style={{ 
                                                    padding: '0.75rem', 
                                                    backgroundColor: 'var(--background-primary)', 
                                                    borderRadius: '6px',
                                                    marginBottom: '0.75rem'
                                                }}>
                                                    <div style={{ fontWeight: 'bold', color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
                                                        {t('ROUTE')}
                                                    </div>
                                                    <div style={{ fontFamily: 'monospace', fontSize: '0.95rem', color: 'var(--text-secondary)' }}>
                                                        {activeFlight.simbriefData.route || 'DCT'}
                                                    </div>
                                                </div>
                                                
                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem' }}>
                                                    <div style={{ textAlign: 'center', padding: '0.5rem', backgroundColor: 'var(--background-primary)', borderRadius: '4px' }}>
                                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>{t('CRUISE')}</div>
                                                        <div style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>
                                                            FL{Math.round(parseInt(activeFlight.simbriefData.cruiseAltitude || 0) / 100) || '---'}
                                                        </div>
                                                    </div>
                                                    <div style={{ textAlign: 'center', padding: '0.5rem', backgroundColor: 'var(--background-primary)', borderRadius: '4px' }}>
                                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>{t('CI')}</div>
                                                        <div style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>
                                                            {activeFlight.simbriefData.costIndex || '---'}
                                                        </div>
                                                    </div>
                                                    <div style={{ textAlign: 'center', padding: '0.5rem', backgroundColor: 'var(--background-primary)', borderRadius: '4px' }}>
                                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>{t('PAX/CARGO')}</div>
                                                        <div style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>
                                                            {activeFlight.simbriefData.weights?.pax_count || 0}/{Math.round((activeFlight.simbriefData.weights?.cargo || 0)/1000)}T
                                                        </div>
                                                    </div>
                                                    <div style={{ textAlign: 'center', padding: '0.5rem', backgroundColor: 'var(--background-primary)', borderRadius: '4px' }}>
                                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>{t('BLOCK')}</div>
                                                        <div style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>
                                                            {Math.floor((activeFlight.simbriefData.times?.estimated_block || 0) / 3600)}:{String(Math.floor(((activeFlight.simbriefData.times?.estimated_block || 0) % 3600) / 60)).padStart(2, '0')}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* Fuel Information */}
                                            <div style={{ 
                                                marginTop: '1rem',
                                                padding: '0.75rem',
                                                backgroundColor: 'var(--background-primary)',
                                                borderRadius: '6px',
                                                border: '1px solid var(--border-color)'
                                            }}>
                                                <h5 style={{ margin: '0 0 0.75rem 0', color: 'var(--text-primary)', fontSize: '1rem' }}>
                                                    ⛽ {t('Fuel Planning')}
                                                </h5>
                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', fontSize: '0.9rem' }}>
                                                    <div style={{ textAlign: 'center', padding: '0.5rem', backgroundColor: 'var(--background-secondary)', borderRadius: '4px' }}>
                                                        <div style={{ fontWeight: 'bold', color: 'var(--accent-primary)', fontSize: '1.2rem' }}>
                                                            {activeFlight.simbriefData.fuel?.plan_ramp || 0} kg
                                                        </div>
                                                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{t('Block Fuel')}</div>
                                                    </div>
                                                    <div style={{ textAlign: 'center', padding: '0.5rem', backgroundColor: 'var(--background-secondary)', borderRadius: '4px' }}>
                                                        <div style={{ fontWeight: 'bold', color: 'var(--text-primary)', fontSize: '1.2rem' }}>
                                                            {Math.round((activeFlight.simbriefData.fuel?.plan_takeoff - activeFlight.simbriefData.fuel?.plan_landing) || 0)} kg
                                                        </div>
                                                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{t('Trip Fuel')}</div>
                                                    </div>
                                                    <div style={{ textAlign: 'center', padding: '0.5rem', backgroundColor: 'var(--background-secondary)', borderRadius: '4px' }}>
                                                        <div style={{ fontWeight: 'bold', color: 'var(--text-primary)', fontSize: '1.2rem' }}>
                                                            {activeFlight.simbriefData.fuel?.plan_landing || 0} kg
                                                        </div>
                                                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{t('Landing Fuel')}</div>
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid var(--border-color)', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                                    <span>{t('Reserve')}: {activeFlight.simbriefData.fuel?.reserve || 0} kg</span> • 
                                                    <span> {t('Contingency')}: {activeFlight.simbriefData.fuel?.contingency || 0} kg</span> • 
                                                    <span> {t('Alternate')}: {activeFlight.simbriefData.fuel?.alternate || 0} kg</span>
                                                    {activeFlight.simbriefData.fuel?.extra > 0 && (
                                                        <span> • {t('Extra')}: {activeFlight.simbriefData.fuel?.extra || 0} kg</span>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            {activeFlight.simbriefData.files?.pdf && (
                                                <div style={{ marginTop: '1rem' }}>
                                                    <button 
                                                        className="btn"
                                                        onClick={() => window.open(`https://www.simbrief.com/ofp/flightplans/${activeFlight.simbriefData.files.pdf}`, '_blank')}
                                                    >
                                                        <FontAwesomeIcon icon={faFileExport} /> {t('View OFP PDF')}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                        
                                </div>
                            </>
                        ) : !isFromTour ? (
                            <div className="flight-modes-container">
                                {/* Main Flight Modes */}
                                <h3 className="section-title">{t('Elige tu modo de vuelo') || 'Elige tu modo de vuelo'}</h3>
                                <div className="flight-modes-grid">
                                    {config.ALLOW_FREE_MODE && (
                                        <div className={`flight-mode-card ${selectedButton === 'free' ? 'active' : ''}`}
                                             onClick={() => handleButtonClick('free')}>
                                            <div className="mode-icon">
                                                <FontAwesomeIcon icon={faPlaneCircleXmark} />
                                            </div>
                                            <h3>{t('free-mode')}</h3>
                                            <p>{t('free-mode-description') || 'Vuela a cualquier destino sin restricciones. Elige tu aeronave, ruta y horario libremente.'}</p>
                                            <button className="btn">
                                                {selectedButton === 'free' ? t('Seleccionado') : t('Seleccionar')}
                                            </button>
                                        </div>
                                    )}
                                    
                                    {config.ALLOW_CHARTER && (
                                        <div className={`flight-mode-card ${selectedButton === 'charter' ? 'active' : ''}`}
                                             onClick={() => handleButtonClick('charter')}>
                                            <div className="mode-icon">
                                                <FontAwesomeIcon icon={faPlaneArrival} />
                                            </div>
                                            <h3>{t('flight-charter')}</h3>
                                            <p>{t('charter-description') || 'Vuelos charter disponibles desde tu ubicación actual. Requiere flota asignada.'}</p>
                                            <button className="btn">
                                                {selectedButton === 'charter' ? t('Seleccionado') : t('Seleccionar')}
                                            </button>
                                        </div>
                                    )}
                                    
                                    {config.ALLOW_REGULAR && (
                                        <div className={`flight-mode-card ${selectedButton === 'regular' ? 'active' : ''}`}
                                             onClick={() => handleButtonClick('regular')}>
                                            <div className="mode-icon">
                                                <FontAwesomeIcon icon={faPlaneDeparture} />
                                            </div>
                                            <h3>{t('flight-regular')}</h3>
                                            <p>{t('regular-description') || 'Vuelos regulares programados desde tu posición. Requiere estar en el aeropuerto de salida.'}</p>
                                            <button className="btn">
                                                {selectedButton === 'regular' ? t('Seleccionado') : t('Seleccionar')}
                                            </button>
                                        </div>
                                    )}
                                    
                                    {config.ALLOW_REGULAR && (
                                        <div className={`flight-mode-card ${selectedButton === 'dispatcher' ? 'active' : ''}`}
                                             onClick={() => handleButtonClick('dispatcher')}>
                                            <div className="mode-icon">
                                                <FontAwesomeIcon icon={faBusinessTime} />
                                            </div>
                                            <h3>{t('dispatcher')}</h3>
                                            <p>{t('dispatcher-description') || 'Busca y filtra vuelos disponibles en toda la red de rutas.'}</p>
                                            <button className="btn">
                                                {selectedButton === 'dispatcher' ? t('Seleccionado') : t('Seleccionar')}
                                            </button>
                                        </div>
                                    )}
                                </div>
                                
                                {/* Secondary Options */}
                                <h3 className="section-title secondary">{t('Otras opciones') || 'Otras opciones'}</h3>
                                <div className="secondary-options">
                                    <div className="secondary-option-card" 
                                         onClick={() => handleButtonClick('fleet')}>
                                        <div className="option-icon">
                                            <FontAwesomeIcon icon={faPlaneCircleExclamation} />
                                        </div>
                                        <div className="option-content">
                                            <h4>{t('fleet')}</h4>
                                            <p>{t('fleet-description') || 'Consulta las aeronaves disponibles en tu ubicación actual'}</p>
                                        </div>
                                    </div>
                                    
                                    {config.ALLOW_MANUAL_REPORT && (
                                        <div className="secondary-option-card" 
                                             onClick={() => handleButtonClick('manual')}>
                                            <div className="option-icon">
                                                <FontAwesomeIcon icon={faFilePen} />
                                            </div>
                                            <div className="option-content">
                                                <h4>{t('manual-report')}</h4>
                                                <p>{t('manual-report-description') || 'Reporta vuelos realizados fuera del sistema o con ACARS externos'}</p>
                                            </div>
                                        </div>
                                    )}
                                    
                                    <div className="secondary-option-card" 
                                         onClick={() => handleButtonClick('ivao')}>
                                        <div className="option-icon">
                                            <FontAwesomeIcon icon={faDownload} />
                                        </div>
                                        <div className="option-content">
                                            <h4>{t('import-from-ivao')}</h4>
                                            <p>{t('ivao-import-description-short') || 'Importa tus vuelos históricos desde IVAO tracker'}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </>
                )}
            </div>
            <div className="manager-content">
                {selectedButton && !activeFlight && <>{buttonContent[selectedButton]}</>}
            </div>
        </>
    );
}